import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Lock, Calculator, RotateCcw } from 'lucide-react';
import {
  NAVY, NAVY_SOFT, PageAssu, encadreErreur, titreEtape, numeroEtape,
  euros, pourcent, montantSaisi, calculerPaie, dirhams, TAUX_MAD
} from './FormuleAssu';

// Calcul de la paie d'un employé : on coche la formule, on saisit le chiffre
// d'affaires TTC du mois, le détail et le total s'affichent.
// La règle de calcul vient des formules elles-mêmes (champ `remuneration` de
// backend/formules_assu.js) : elle ne peut donc pas diverger des barèmes publiés.

// Clé propre au calculateur, distincte de celle des conditions : recevoir le
// lien du candidat n'ouvre pas les règles de paie. Elle reste dans l'adresse,
// pour que le lien fonctionne au rafraîchissement comme en favori.
const CLE_STOCKAGE = 'cle_paie_assu';

const memoriserCle = (valeur) => {
  try { localStorage.setItem(CLE_STOCKAGE, valeur); } catch (_) { /* navigation privée */ }
  try { sessionStorage.setItem(CLE_STOCKAGE, valeur); } catch (_) { /* ignoré */ }
};

const lireCleInitiale = () => {
  try {
    // Adresse courte `/p/<clé>`, ou forme `?k=<clé>`.
    const dansChemin = window.location.pathname.match(/^\/p\/([^/?#]+)/);
    const depuisUrl = (dansChemin && decodeURIComponent(dansChemin[1]))
      || new URLSearchParams(window.location.search).get('k');
    if (depuisUrl) {
      memoriserCle(depuisUrl);
      return depuisUrl;
    }
    let gardee = '';
    try { gardee = sessionStorage.getItem(CLE_STOCKAGE) || ''; } catch (_) { /* ignoré */ }
    if (!gardee) {
      try { gardee = localStorage.getItem(CLE_STOCKAGE) || ''; } catch (_) { /* ignoré */ }
    }
    return gardee;
  } catch (_) {
    return '';
  }
};

function CalculPaie() {
  const API_URL = process.env.REACT_APP_API_URL || '';

  const [code, setCode] = useState(lireCleInitiale);
  const [saisieCode, setSaisieCode] = useState('');
  const [codeOk, setCodeOk] = useState(null);
  const [formules, setFormules] = useState(null);

  const [employe, setEmploye] = useState('');
  const [choix, setChoix] = useState(null);
  const [ca, setCa] = useState('');

  useEffect(() => {
    if (!code) { setCodeOk(false); return; }
    axios.get(`${API_URL}/api/candidats-assu/formules`, {
      params: { page: 'paie' }, headers: { 'X-Cle-Acces': code }
    })
      .then(res => {
        const prets = (res.data.formules || []).filter(f => f.remuneration);
        setFormules(prets);
        setCodeOk(true);
        if (prets.length === 1) setChoix(prets[0].id);
      })
      .catch(err => {
        if (err.response?.status === 403) setCodeOk(false);
        else { setFormules([]); setCodeOk(true); }
      });
  }, [API_URL, code]);

  if (codeOk === false) {
    return (
      <PageAssu titre="Calcul des paies" sousTitre="Accès réservé">
        <div className="dca-card">
          <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start', marginBottom: 16 }}>
            <Lock size={20} style={{ color: NAVY, flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 14.5 }}>
              Ouvrez cette page depuis le lien complet copié dans le CRM, ou collez ici sa clé d'accès.
            </div>
          </div>
          <form onSubmit={(e) => {
            e.preventDefault();
            const propre = saisieCode.trim();
            setCodeOk(null);
            memoriserCle(propre);
            setCode(propre);
          }}>
            <input
              className="dca-champ"
              type="text"
              autoFocus
              placeholder="Clé d'accès"
              value={saisieCode}
              onChange={(e) => setSaisieCode(e.target.value)}
              style={{ marginBottom: 13, fontWeight: 600 }}
            />
            <button type="submit" className="dca-btn" style={{ width: '100%', justifyContent: 'center', background: NAVY, color: '#fff' }}>
              Accéder
            </button>
          </form>
          {code && <div style={{ ...encadreErreur, marginBottom: 0 }}>Clé invalide ou expirée.</div>}
        </div>
      </PageAssu>
    );
  }

  if (formules === null) {
    return (
      <PageAssu titre="Calcul des paies" sousTitre="Un instant…">
        <div className="dca-card" style={{ textAlign: 'center', color: '#5f6b85' }}>
          <Loader2 size={26} className="dca-spin" style={{ color: NAVY }} />
          <div style={{ marginTop: 10 }}>Chargement des formules…</div>
        </div>
      </PageAssu>
    );
  }

  const formule = formules.find(f => f.id === choix) || null;
  const calcul = formule && montantSaisi(ca) !== null
    ? calculerPaie(formule.remuneration, ca)
    : null;

  const ligne = (label, valeur, gras = false) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 14, padding: '9px 0',
      borderTop: '1px solid #eef0f6', fontSize: 14.5
    }}>
      <span style={{ color: '#5f6b85' }}>{label}</span>
      <span style={{ fontWeight: gras ? 800 : 600, whiteSpace: 'nowrap' }}>{valeur}</span>
    </div>
  );

  return (
    <PageAssu titre="Calcul des paies" sousTitre="Formule, chiffre d'affaires, rémunération">
      <div style={titreEtape}><span style={numeroEtape}>1</span> La formule de l'employé</div>
      {formules.length === 0 ? (
        <div className="dca-card" style={{ color: '#5f6b85', fontSize: 14.5 }}>
          Aucune formule n'a de règle de rémunération renseignée.
        </div>
      ) : (
        <div className="dca-card">
          {formules.map(f => (
            <label
              key={f.id}
              style={{
                display: 'flex', gap: 11, alignItems: 'flex-start', cursor: 'pointer',
                padding: '12px 13px', borderRadius: 12, marginBottom: 8,
                border: `2px solid ${choix === f.id ? NAVY : '#e7eaf3'}`,
                background: choix === f.id ? NAVY_SOFT : '#fff'
              }}
            >
              <input
                type="radio"
                name="formule"
                checked={choix === f.id}
                onChange={() => setChoix(f.id)}
                style={{ width: 18, height: 18, marginTop: 2, accentColor: NAVY, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 800, fontSize: 15.5, color: f.couleur || NAVY }}>{f.nom}</div>
                <div style={{ fontSize: 13, color: '#5f6b85', marginTop: 2 }}>
                  {f.remuneration.fixe > 0
                    ? `Fixe de ${euros(f.remuneration.fixe)}, puis ${pourcent(f.remuneration.taux)} au-delà de ${euros(f.remuneration.seuil)}`
                    : `Pas de fixe, ${pourcent(f.remuneration.taux)} dès le premier euro`}
                  {' '}· base diminuée de {pourcent(f.remuneration.retenue)}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      <div style={{ ...titreEtape, opacity: choix ? 1 : 0.45 }}>
        <span style={numeroEtape}>2</span> Le chiffre d'affaires du mois
      </div>
      <div className="dca-card">
        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 5 }}>
          Employé (facultatif)
        </label>
        <input
          className="dca-champ"
          type="text"
          placeholder="Nom de l'employé"
          value={employe}
          onChange={(e) => setEmploye(e.target.value)}
          style={{ marginBottom: 14 }}
        />
        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 5 }}>
          Chiffre d'affaires TTC réalisé
        </label>
        <input
          className="dca-champ"
          type="text"
          inputMode="decimal"
          placeholder="10 000"
          value={ca}
          onChange={(e) => setCa(e.target.value)}
          style={{ fontSize: 20, fontWeight: 700 }}
        />
        <div style={{ fontSize: 12.5, color: '#8a94ad', marginTop: 6 }}>
          En euros TTC. Le calcul se met à jour à mesure que vous tapez.
        </div>
      </div>

      <div style={{ ...titreEtape, opacity: calcul ? 1 : 0.45 }}>
        <span style={numeroEtape}>3</span> La rémunération
      </div>
      {!calcul ? (
        <div className="dca-card" style={{ color: '#5f6b85', fontSize: 14.5 }}>
          Cochez une formule et saisissez un chiffre d'affaires, le détail s'affichera ici.
        </div>
      ) : (
        <>
          <div className="dca-card" style={{ background: NAVY, color: '#fff', border: 'none' }}>
            <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 700 }}>
              {employe ? employe.toUpperCase() : 'À VERSER'} · {formule.nom}
            </div>
            <div className="dca-total">{dirhams(calcul.total)}</div>
            <div style={{ fontSize: 19, fontWeight: 700, opacity: 0.92 }}>
              soit {euros(calcul.total)}
            </div>
            <div style={{ fontSize: 13.5, opacity: 0.85, marginTop: 4 }}>
              pour {euros(calcul.ca)} de chiffre d'affaires TTC · conversion ×{TAUX_MAD}
            </div>
          </div>

          <div className="dca-card">
            <div style={{ fontWeight: 800, color: NAVY, fontSize: 15, marginBottom: 4 }}>Le détail</div>
            {ligne('Chiffre d\'affaires TTC', euros(calcul.ca))}
            {calcul.seuil > 0 && ligne('Seuil de déclenchement', `− ${euros(calcul.seuil)}`)}
            {calcul.seuil > 0 && ligne('Part au-dessus du seuil', euros(calcul.tranche))}
            {ligne(`Déduction de ${pourcent(calcul.retenue)}`, `− ${euros(calcul.deduction)}`)}
            {ligne('Base de calcul', euros(calcul.base))}
            {ligne(`Commission à ${pourcent(calcul.taux)}`, euros(calcul.commission))}
            {calcul.fixe > 0 && ligne('Fixe mensuel', euros(calcul.fixe))}
            <div style={{
              display: 'flex', justifyContent: 'space-between', gap: 14, padding: '12px 0 0',
              borderTop: `2px solid ${NAVY}`, marginTop: 6, fontSize: 17
            }}>
              <span style={{ fontWeight: 800, color: NAVY }}>Total à verser</span>
              <span style={{ fontWeight: 800, color: NAVY, textAlign: 'right' }}>
                {dirhams(calcul.total)}
                <div style={{ fontSize: 13, fontWeight: 600, color: '#5f6b85' }}>{euros(calcul.total)}</div>
              </span>
            </div>

            {calcul.seuil > 0 && calcul.tranche === 0 && (
              <div style={{
                marginTop: 14, background: '#fdfaf3', border: '1px solid #e6c98a',
                borderRadius: 10, padding: '11px 13px', fontSize: 14
              }}>
                Le seuil de {euros(calcul.seuil)} n'est pas atteint : aucune commission,
                seul le fixe est dû.
              </div>
            )}
          </div>

          <div className="dca-card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 0 }}>
            <Calculator size={18} style={{ color: '#5f6b85', flexShrink: 0 }} />
            <span style={{ flex: '1 1 200px', fontSize: 13.5, color: '#5f6b85' }}>
              Calcul suivant : effacez le chiffre d'affaires et saisissez le suivant.
            </span>
            <button
              className="dca-btn"
              onClick={() => { setCa(''); setEmploye(''); }}
              style={{ background: '#fff', color: NAVY, border: `1.5px solid ${NAVY}`, padding: '9px 15px', fontSize: 14 }}
            >
              <RotateCcw size={15} /> Nouveau calcul
            </button>
          </div>
        </>
      )}
    </PageAssu>
  );
}

export default CalculPaie;
